import React from "react";
import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { getImageUrl, getStoryTitle } from "../lib/screenshotApi";
import ScreenshotAppChrome from "./ScreenshotAppChrome";

const PILLS = [
    { id: "all", label: "All", emoji: "🏷️" },
    { id: "animal", label: "Animal", emoji: "🐾" },
    { id: "travel", label: "Travel", emoji: "✈️" },
    { id: "art", label: "Art", emoji: "🎨" },
    { id: "nature", label: "Nature", emoji: "🌳" },
    { id: "sport", label: "Sport", emoji: "⚽" },
    { id: "interior", label: "Interior", emoji: "🛋️" },
];

const TABS = [
    { id: "browse", label: "Browse all" },
    { id: "progress", label: "In Progress" },
    { id: "complete", label: "Complete" },
];

function isFreePuzzle(item) {
    const bundle = item?.group?.storySliding ?? item?.group?.story_sliding;
    if (bundle && typeof bundle.free === "boolean") return bundle.free;
    return false;
}

function PuzzleTile({ item }) {
    const story = item?.group?.story;
    const imageUrl = getImageUrl(story);
    const title = getStoryTitle(story);
    const free = isFreePuzzle(item);

    return (
        <Box
            sx={{
                minWidth: 0,
                minHeight: 0,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: "12px",
                overflow: "hidden",
                bgcolor: "#ffffff",
                border: "1px solid rgba(15, 23, 42, 0.08)",
            }}
        >
            <Box sx={{ position: "relative", width: "100%", flex: "1 1 auto", minHeight: 0, bgcolor: "rgba(15, 23, 42, 0.05)" }}>
                {imageUrl ? (
                    <Box
                        component="img"
                        src={imageUrl}
                        alt=""
                        draggable={false}
                        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                ) : null}
                <Box
                    sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        px: 1,
                        py: 0.35,
                        borderRadius: "6px",
                        bgcolor: free ? "rgba(22,163,74,0.9)" : "rgba(202,138,4,0.92)",
                    }}
                >
                    <Typography
                        component="span"
                        sx={{ color: "#fff", fontSize: 10, fontWeight: 800, letterSpacing: "0.4px", lineHeight: 1.2 }}
                    >
                        {free ? "FREE" : "PREMIUM"}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ px: 1.25, py: 0.75, flexShrink: 0 }}>
                <Typography
                    component="p"
                    sx={{
                        m: 0,
                        fontSize: 13,
                        fontWeight: 600,
                        lineHeight: "17px",
                        color: "#0f1419",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: 34,
                    }}
                >
                    {title}
                </Typography>
            </Box>
        </Box>
    );
}

PuzzleTile.propTypes = {
    item: PropTypes.object,
};

/**
 * Android Puzzles tab: browse segments, category pills, and a two-column grid.
 */
export default function ScreenshotPuzzlesScene({ items }) {
    const puzzles = Array.isArray(items) ? items.slice(0, 6) : [];

    return (
        <ScreenshotAppChrome active="library" title="Puzzles">
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <Box sx={{ px: 1.5, pt: 2, pb: 0.5, flexShrink: 0 }}>
                    <Box
                        sx={{
                            display: "flex",
                            minHeight: 44,
                            p: "3px",
                            borderRadius: "10px",
                            border: "1px solid rgba(15, 23, 42, 0.1)",
                            bgcolor: "rgba(15, 23, 42, 0.08)",
                        }}
                    >
                        {TABS.map((tab) => {
                            const selected = tab.id === "browse";
                            return (
                                <Box
                                    key={tab.id}
                                    sx={{
                                        flex: 1,
                                        minHeight: 38,
                                        borderRadius: "8px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        px: 0.5,
                                        bgcolor: selected ? "#ffffff" : "transparent",
                                    }}
                                >
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: 13,
                                            fontWeight: selected ? 800 : 700,
                                            lineHeight: 1.2,
                                            color: selected ? "#0f1419" : "rgba(15, 23, 42, 0.55)",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {tab.label}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        overflow: "hidden",
                        px: 1.5,
                        pt: 1,
                        pb: 1.5,
                        flexShrink: 0,
                    }}
                >
                    {PILLS.map((pill) => {
                        const selected = pill.id === "all";
                        return (
                            <Box
                                key={pill.id}
                                sx={{
                                    flexShrink: 0,
                                    height: 36,
                                    pl: 1.25,
                                    pr: 1.75,
                                    borderRadius: 999,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    bgcolor: selected ? "#3BA666" : "rgba(255,255,255,0.92)",
                                    border: selected ? "1px solid #3BA666" : "1px solid rgba(15,23,42,0.12)",
                                }}
                            >
                                <Typography component="span" sx={{ fontSize: 16, lineHeight: "20px" }}>
                                    {pill.emoji}
                                </Typography>
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: 14,
                                        fontWeight: 600,
                                        lineHeight: 1,
                                        color: selected ? "#ffffff" : "#0f1419",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {pill.label}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                <Box
                    sx={{
                        flex: "1 1 auto",
                        minHeight: 0,
                        overflow: "hidden",
                        px: 1.5,
                        pb: 1.5,
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        gridTemplateRows: "repeat(3, minmax(0, 1fr))",
                        gap: 1.25,
                    }}
                >
                    {puzzles.map((item) => (
                        <PuzzleTile key={item.storyId || getStoryTitle(item?.group?.story)} item={item} />
                    ))}
                </Box>
            </Box>
        </ScreenshotAppChrome>
    );
}

ScreenshotPuzzlesScene.propTypes = {
    items: PropTypes.arrayOf(PropTypes.object),
};
